import DriverApplicationReview from "@/components/DriverApplicationReview";
import { useState, useEffect, useCallback, useRef } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import {
  Car, Bike, Truck, User, Clock, CheckCircle, XCircle,
  RefreshCw, AlertTriangle, Banknote, Users, ClipboardList, Search, Star, Map, Mail, Send, Package, RotateCcw, Navigation, MessageCircle, X, PlusCircle,
} from "lucide-react";
import AvailableDriversMap from "@/components/AvailableDriversMap";
import { WebAvatar } from "@/components/WebAvatar";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function pence(p: number) {
  return `£${(p / 100).toFixed(2)}`;
}

function elapsed(since: string) {
  const ms = Date.now() - new Date(since).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function vehicleIcon(v: string) {
  if (v === "van") return <Truck size={14} />;
  if (v === "car") return <Car size={14} />;
  return <Bike size={14} />;
}

function vehicleLabel(v: string) {
  return v.charAt(0).toUpperCase() + v.slice(1);
}

function initials(name: string | null) {
  if (!name) return "?";
  return name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
}

interface EarningsSummary {
  pendingApplications: number;
  activeDrivers: number;
  thisWeekPence: number;
  thisMonthPence: number;
}

interface Application {
  id: number;
  userId: number;
  vehicleType: string;
  licenceNumber: string | null;
  bio: string | null;
  isAvailable: boolean;
  approvedAt: string | null;
  rejectedAt: string | null;
  adminNotes: string | null;
  isHighValueTrusted: boolean;
  createdAt: string;
  userName: string | null;
  userEmail: string | null;
  userPhone: string | null;
  userTown: string | null;
}

interface ActiveDriver {
  homeAddress?: { line1: string; line2: string; town: string; postcode: string; confirmedAt: string } | null;
  userId: number;
  vehicleType: string;
  bio: string | null;
  isAvailable: boolean;
  isOnline: boolean;
  lastLat: number | null;
  lastLng: number | null;
  lastLocationAt: string | null;
  lastSeenAt: string | null;
  approvedAt: string | null;
  adminNotes: string | null;
  isHighValueTrusted: boolean;
  userName: string | null;
  userEmail: string | null;
  userPhone: string | null;
  userAvatarUrl: string | null;
  userTown: string | null;
  isDriverActive: boolean | null;
  completedCount: number;
  todayDeliveryCount: number;
  todayEarningsPence: number;
  avgRating: number | null;
  ratingCount: number;
}

interface DispatchFailure {
  orderId: number;
  storeId: number | null;
  storeName: string | null;
  deliveryAddress: string | null;
  totalPence: number;
  deliveryFeePence: number;
  createdAt: string;
  updatedAt: string | null;
  customerId: number | null;
  customerName: string | null;
}

interface LiveDelivery {
  orderId: number;
  storeId: number | null;
  storeName: string | null;
  customerId: number | null;
  deliveryAddress: string | null;
  totalPence: number;
  deliveryFeePence: number;
  createdAt: string;
  status: string;
  driverId: number | null;
  pickedUpAt: string | null;
  earningsPence: number | null;
  assignmentId: number | null;
  driverName: string | null;
  customerPaidPence: number;
  items: Array<{
    name: string;
    quantity: number;
    pricePence: number;
    selectedOptions: string | null;
  }>;
}

function optionNames(selectedOptions: string | null): string[] {
  if (!selectedOptions) return [];
  try {
    const options = JSON.parse(selectedOptions) as Array<{ optionName?: string }>;
    return [...new Set(options.map((option) => option.optionName).filter((name): name is string => !!name))];
  } catch {
    return [];
  }
}

interface HistoryRow {
  orderId: number;
  deliveredAt: string | null;
  earningsPence: number;
  storeName: string | null;
  totalPence: number | null;
}

const CREDIT_REASONS = [
  "Shop mistake",
  "Goodwill gesture",
  "Admin bonus",
  "Late order compensation",
  "Navigation issue",
  "Delivery dispute resolution",
  "Fuel compensation",
  "Other",
] as const;

interface DriverCreditRow {
  id: number;
  amountPence: number;
  reason: string;
  note: string | null;
  createdByName: string | null;
  createdAt: string;
}

type Tab = "applications" | "active" | "live" | "dispatch" | "map" | "hubdash";

type HubDashItem = {
  id: number;
  status: string;
  customerName: string;
  pickupAddress: string;
  dropoffAddress: string;
  distanceMiles: number;
  quotedFeePence: number;
  driverEarningsPence: number;
  platformFeePence: number;
  tipPence: number;
  grossTipPence?:number;
  rating: number | null;
  driverName: string | null;
  createdAt: string;
};

export default function AdminDrivers() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();

  const [tab, setTab] = useState<Tab>("applications");
  const [summary, setSummary] = useState<EarningsSummary | null>(null);
  const [applications, setApplications] = useState<Application[]>([]);
  const [activeDrivers, setActiveDrivers] = useState<ActiveDriver[]>([]);
  const [liveDeliveries, setLiveDeliveries] = useState<LiveDelivery[]>([]);
  const [hubDashItems, setHubDashItems] = useState<HubDashItem[]>([]);
  const [hubDashLoading, setHubDashLoading] = useState(false);
  const [hubDashRefundingId, setHubDashRefundingId] = useState<number | null>(null);
  const [hubDashReassigningId, setHubDashReassigningId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const [drawerDriver, setDrawerDriver] = useState<ActiveDriver | null>(null);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [deactivateConfirm, setDeactivateConfirm] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const [rejectUserId, setRejectUserId] = useState<number | null>(null);
  const [rejectNotes, setRejectNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const [driverSearch, setDriverSearch] = useState("");
  const [driverVehicleFilter, setDriverVehicleFilter] = useState("all");
  const [driverStatusFilter, setDriverStatusFilter] = useState("all");

  const [dispatchFailures, setDispatchFailures] = useState<DispatchFailure[]>([]);
  const liveRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  const [msgTarget, setMsgTarget] = useState<{ driverId: number; driverName: string } | null>(null);
  const [msgBody, setMsgBody] = useState("");
  const [sendingMsg, setSendingMsg] = useState(false);

  const [creditTarget, setCreditTarget] = useState<{ driverId: number; driverName: string } | null>(null);
  const [creditFormOpen, setCreditFormOpen] = useState(false);
  const [creditAmountPounds, setCreditAmountPounds] = useState("");
  const [creditReason, setCreditReason] = useState<string>(CREDIT_REASONS[0]);
  const [creditNote, setCreditNote] = useState("");
  const [submittingCredit, setSubmittingCredit] = useState(false);
  const [driverCredits, setDriverCredits] = useState<DriverCreditRow[]>([]);
  const [loadingCredits, setLoadingCredits] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await fetch("/api/admin/drivers/earnings-summary", { headers: authHeaders() });
      const data = await res.json() as EarningsSummary;
      setSummary(data);
    } catch {
      // silent
    }
  }, []);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/driver-applications?status=pending", { headers: authHeaders() });
      const data = await res.json() as { items: Application[] };
      setApplications(data.items ?? []);
    } catch {
      toast({ title: "Failed to load applications", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchActiveDrivers = useCallback(async (search = "", vehicleType = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (vehicleType && vehicleType !== "all") params.set("vehicleType", vehicleType);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await fetch(`/api/admin/drivers${qs}`, { headers: authHeaders() });
      const data = await res.json() as { items: ActiveDriver[] };
      setActiveDrivers(data.items ?? []);
    } catch {
      toast({ title: "Failed to load drivers", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchLive = useCallback(async () => {
    try {
      const res = await fetch("/api/admin/drivers/live-deliveries", { headers: authHeaders() });
      const data = await res.json() as { items: LiveDelivery[] };
      setLiveDeliveries(data.items ?? []);
    } catch {
      // silent on background refresh
    }
  }, []);

  const fetchDispatchFailures = useCallback(async () => {
    try {
      const res = await fetch("/api/admin/orders/dispatch-failures", { headers: authHeaders() });
      const data = await res.json() as { items: DispatchFailure[] };
      setDispatchFailures(data.items ?? []);
    } catch {
      // silent
    }
  }, []);

  const fetchHubDash = useCallback(async () => {
    setHubDashLoading(true);
    try {
      const res = await fetch("/api/admin/item-delivery-requests?limit=100", { headers: authHeaders() });
      if (res.ok) {
        const d = await res.json() as { requests: HubDashItem[] };
        setHubDashItems(d.requests);
      }
    } catch { /* silent */ }
    finally { setHubDashLoading(false); }
  }, []);

  useEffect(() => {
    if (authLoading || user?.role !== "admin") return;
    void fetchSummary();
  }, [fetchSummary, authLoading, user?.role]);

  const handleForceOnline = async (userId: number, online: boolean) => {
    try {
      const res = await fetch(`/api/admin/drivers/${userId}/set-online`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ online }),
      });
      if (!res.ok) throw new Error();
      setActiveDrivers((prev) =>
        prev.map((d) => d.userId === userId ? { ...d, isOnline: online, isAvailable: online } : d)
      );
      toast({ title: online ? "Driver set online" : "Driver set offline" });
    } catch {
      toast({ title: "Failed to update driver status", variant: "destructive" });
    }
  };

  useEffect(() => {
    if (authLoading || user?.role !== "admin") return;
    if (tab === "applications") void fetchApplications();
    if (tab === "active") void fetchActiveDrivers();
    if (tab === "map") {
      setLoading(true);
      void fetchActiveDrivers().finally(() => setLoading(false));
      liveRefreshRef.current = setInterval(() => void fetchActiveDrivers(), 30000);
    }
    if (tab === "live") {
      setLoading(true);
      void fetchLive().finally(() => setLoading(false));
      liveRefreshRef.current = setInterval(() => void fetchLive(), 15000);
    }
    if (tab === "dispatch") {
      setLoading(true);
      void fetchDispatchFailures().finally(() => setLoading(false));
    }
    if (tab === "hubdash") {
      void fetchHubDash();
      liveRefreshRef.current = setInterval(() => void fetchHubDash(), 15000);
    }
    return () => {
      if (liveRefreshRef.current) clearInterval(liveRefreshRef.current);
    };
  }, [tab, fetchApplications, fetchActiveDrivers, fetchLive, fetchDispatchFailures, fetchHubDash, authLoading, user?.role]);

  if (authLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;

  const filteredDrivers = activeDrivers.filter((d) => {
    if (driverSearch) {
      const q = driverSearch.toLowerCase();
      const nameMatch = (d.userName ?? "").toLowerCase().includes(q);
      const emailMatch = (d.userEmail ?? "").toLowerCase().includes(q);
      const homeMatch = `${d.homeAddress?.town ?? ""} ${d.homeAddress?.postcode ?? ""}`.toLowerCase().includes(q);
      if (!nameMatch && !emailMatch && !homeMatch) return false;
    }
    if (driverVehicleFilter !== "all" && d.vehicleType !== driverVehicleFilter) return false;
    if (driverStatusFilter === "available" && !(d.isDriverActive && d.isAvailable)) return false;
    if (driverStatusFilter === "offline" && !(d.isDriverActive && !d.isAvailable)) return false;
    if (driverStatusFilter === "deactivated" && d.isDriverActive !== false) return false;
    return true;
  });

  const handleApprove = async (userId: number) => {
    setBusy(true);
    try {
      const res = await fetch(`/api/admin/drivers/${userId}/approve`, { method: "PATCH", headers: authHeaders() });
      if (!res.ok) throw new Error();
      toast({ title: "Driver approved" });
      setApplications((prev) => prev.filter((a) => a.userId !== userId));
      void fetchSummary();
    } catch {
      toast({ title: "Failed to approve", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    if (rejectUserId == null) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/admin/drivers/${rejectUserId}/reject`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ adminNotes: rejectNotes }),
      });
      if (!res.ok) throw new Error();
      toast({ title: "Application rejected" });
      setApplications((prev) => prev.filter((a) => a.userId !== rejectUserId));
      setRejectUserId(null);
      setRejectNotes("");
      void fetchSummary();
    } catch {
      toast({ title: "Failed to reject", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const handleCancelDelivery = async (orderId: number) => {
    setBusy(true);
    try {
      const res = await fetch(`/api/admin/orders/${orderId}/cancel-delivery`, { method: "PATCH", headers: authHeaders() });
      if (!res.ok) throw new Error();
      toast({ title: "Delivery cancelled — order reset to ready" });
      setLiveDeliveries((prev) => prev.filter((d) => d.orderId !== orderId));
    } catch {
      toast({ title: "Failed to cancel delivery", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const sendDriverMessage = async () => {
    if (!msgTarget || !msgBody.trim()) return;
    setSendingMsg(true);
    try {
      const res = await fetch(`/api/admin/drivers/${msgTarget.driverId}/message`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ title: "Message from Ayrshire Hub", body: msgBody.trim() }),
      });
      const data = await res.json() as { sent?: number; error?: string };
      if (!res.ok) throw new Error(data.error ?? "Failed");
      toast({ title: `Message sent to ${msgTarget.driverName}` });
      setMsgTarget(null);
      setMsgBody("");
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Failed to send message", variant: "destructive" });
    } finally {
      setSendingMsg(false);
    }
  };

  const loadDriverCredits = async (driverId: number) => {
    setLoadingCredits(true);
    try {
      const res = await fetch(`/api/admin/drivers/${driverId}/credits`, { headers: authHeaders() });
      const data = await res.json() as { credits: DriverCreditRow[] };
      setDriverCredits(data.credits ?? []);
    } catch {
      setDriverCredits([]);
    } finally {
      setLoadingCredits(false);
    }
  };

  const submitCredit = async (driverId: number, driverName: string) => {
    const pounds = parseFloat(creditAmountPounds);
    if (isNaN(pounds) || pounds <= 0) {
      toast({ title: "Enter a valid amount", variant: "destructive" });
      return;
    }
    const amountPence = Math.round(pounds * 100);
    setSubmittingCredit(true);
    try {
      const res = await fetch(`/api/admin/drivers/${driverId}/credit`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ amountPence, reason: creditReason, note: creditNote.trim() || undefined, adjustmentKey:(()=>{const key=`driver-credit:${driverId}:${amountPence}:${creditReason}:${creditNote}`;let saved=sessionStorage.getItem(key);if(!saved){saved=crypto.randomUUID();sessionStorage.setItem(key,saved);}return saved;})() }),
      });
      const data = await res.json() as { error?: string };
      if (!res.ok) throw new Error(data.error ?? "Failed");
      toast({ title: `Credit of £${pounds.toFixed(2)} added for ${driverName}` });
      sessionStorage.removeItem(`driver-credit:${driverId}:${amountPence}:${creditReason}:${creditNote}`);
      setCreditFormOpen(false);
      setCreditAmountPounds("");
      setCreditReason(CREDIT_REASONS[0]);
      setCreditNote("");
      void loadDriverCredits(driverId);
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Failed to add credit", variant: "destructive" });
    } finally {
      setSubmittingCredit(false);
    }
  };

  const handleRedispatch = async (orderId: number) => {
    setBusy(true);
    try {
      const res = await fetch(`/api/admin/orders/${orderId}/redispatch`, { method: "POST", headers: authHeaders() });
      if (!res.ok) throw new Error();
      toast({ title: "Re-dispatching order — drivers will be notified" });
      setDispatchFailures((prev) => prev.filter((d) => d.orderId !== orderId));
    } catch {
      toast({ title: "Failed to re-dispatch", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const handleCancelFailedOrder = async (orderId: number) => {
    setBusy(true);
    try {
      const res = await fetch(`/api/admin/orders/${orderId}/cancel-dispatch-failure`, { method: "POST", headers: authHeaders() });
      if (!res.ok) throw new Error();
      toast({ title: "Order reverted — store can now handle collection" });
      setDispatchFailures((prev) => prev.filter((d) => d.orderId !== orderId));
    } catch {
      toast({ title: "Failed to cancel dispatch", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const openDrawer = async (driver: ActiveDriver) => {
    setDrawerDriver(driver);
    setAdminNotes(driver.adminNotes ?? "");
    setDeactivateConfirm(false);
    setDriverCredits([]);
    setHistoryLoading(true);
    try {
      const [histRes] = await Promise.all([
        fetch(`/api/admin/drivers/${driver.userId}/history`, { headers: authHeaders() }),
        loadDriverCredits(driver.userId),
      ]);
      const data = await histRes.json() as { items: HistoryRow[]; totalEarningsPence: number };
      setHistory(data.items ?? []);
      setHistoryTotal(data.totalEarningsPence ?? 0);
    } catch {
      setHistory([]);
      setHistoryTotal(0);
    } finally {
      setHistoryLoading(false);
    }
  };

  const saveNotes = async () => {
    if (!drawerDriver) return;
    setSavingNotes(true);
    try {
      await fetch(`/api/admin/drivers/${drawerDriver.userId}/notes`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ adminNotes }),
      });
    } catch {
      // silent
    } finally {
      setSavingNotes(false);
    }
  };

  const setHighValueTrust = async (trusted: boolean) => {
    if (!drawerDriver) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/admin/drivers/${drawerDriver.userId}/high-value-trust`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ trusted }),
      });
      const data = await res.json() as { error?: string };
      if (!res.ok) throw new Error(data.error ?? "Could not update trust status");
      const updated = { ...drawerDriver, isHighValueTrusted: trusted };
      setDrawerDriver(updated);
      setActiveDrivers(prev => prev.map(driver => driver.userId === updated.userId ? updated : driver));
      toast({ title: trusted ? "Gold Trusted Driver awarded" : "Gold Trusted Driver removed" });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Could not update trust status", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const handleDeactivate = async () => {
    if (!drawerDriver) return;
    setDeactivating(true);
    try {
      const res = await fetch(`/api/admin/drivers/${drawerDriver.userId}/deactivate`, { method: "PATCH", headers: authHeaders() });
      if (!res.ok) throw new Error();
      toast({ title: "Driver deactivated" });
      setDrawerDriver(null);
      void fetchActiveDrivers();
      void fetchSummary();
    } catch {
      toast({ title: "Failed to deactivate", variant: "destructive" });
    } finally {
      setDeactivating(false);
      setDeactivateConfirm(false);
    }
  };

  const handleEmailAll = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) {
      toast({ title: "Subject and message body are required", variant: "destructive" });
      return;
    }
    setSendingEmail(true);
    try {
      const res = await fetch("/api/admin/drivers/email-all", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ subject: emailSubject, body: emailBody }),
      });
      const data = await res.json() as { sent?: number; error?: string };
      if (!res.ok) throw new Error(data.error ?? "Failed to send");
      toast({ title: `Email sent to ${data.sent} driver${data.sent !== 1 ? "s" : ""}` });
      setEmailDialogOpen(false);
      setEmailSubject("");
      setEmailBody("");
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Failed to send email", variant: "destructive" });
    } finally {
      setSendingEmail(false);
    }
  };

  const TAB_ITEMS: { key: Tab; label: string; icon?: React.ReactNode }[] = [
    { key: "applications", label: `Applications${summary ? ` (${summary.pendingApplications})` : ""}` },
    { key: "active", label: `Active Drivers${summary ? ` (${summary.activeDrivers})` : ""}` },
    { key: "live", label: `Live Deliveries${liveDeliveries.length > 0 && tab !== "live" ? ` (${liveDeliveries.length})` : ""}` },
    { key: "dispatch", label: `Dispatch Issues${dispatchFailures.length > 0 && tab !== "dispatch" ? ` (${dispatchFailures.length})` : ""}`, icon: dispatchFailures.length > 0 && tab !== "dispatch" ? <AlertTriangle size={13} className="text-red-500" /> : undefined },
    { key: "map", label: "Driver Map", icon: <Map size={13} /> },
    { key: "hubdash", label: `Hub Dash${hubDashItems.length > 0 && tab !== "hubdash" ? ` (${hubDashItems.length})` : ""}`, icon: <Package size={13} /> },
  ];

  return (
    <AdminLayout current="/admin/drivers">
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl font-bold text-primary">Driver Management</h1>
          <button
            onClick={() => setEmailDialogOpen(true)}
            className="flex items-center gap-1.5 text-sm font-semibold bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Mail size={14} /> Email All Drivers
          </button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <ClipboardList size={13} /> Pending Applications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${(summary?.pendingApplications ?? 0) > 0 ? "text-amber-600" : "text-foreground"}`}>
                {summary?.pendingApplications ?? "—"}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <Users size={13} /> Active Drivers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">{summary?.activeDrivers ?? "—"}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <Banknote size={13} /> This Week
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">
                {summary ? pence(summary.thisWeekPence) : "—"}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <Banknote size={13} /> This Month
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-emerald-600">
                {summary ? pence(summary.thisMonthPence) : "—"}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
          {TAB_ITEMS.map(({ key, label, icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
                tab === key
                  ? "border-primary text-primary"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {icon}{label}
            </button>
          ))}
        </div>

        {/* Applications tab */}
        {tab === "applications" && (
          <div className="space-y-4">
            {loading ? (
              <p className="text-sm text-muted-foreground animate-pulse">Loading applications…</p>
            ) : applications.length === 0 ? (
              <div className="text-center py-16 text-slate-400">
                <CheckCircle size={40} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm font-medium">No pending applications</p>
              </div>
            ) : (
              applications.map((app) => (
                <div key={app.userId} className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
                  <DriverApplicationReview userId={app.userId} />
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm flex-shrink-0">
                        {initials(app.userName)}
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-slate-800">{app.userName ?? "Unknown"}</p>
                        <p className="text-xs text-slate-500">{app.userEmail ?? ""}{app.userPhone ? ` · ${app.userPhone}` : ""}{app.userTown ? ` · ${app.userTown}` : ""}</p>
                        <p className="text-xs text-slate-400">Applied {new Date(app.createdAt).toLocaleDateString("en-GB")}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="flex items-center gap-1 text-xs">
                      {vehicleIcon(app.vehicleType)}
                      {vehicleLabel(app.vehicleType)}
                    </Badge>
                  </div>

                  {app.licenceNumber && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Licence</span>
                      <span className="text-xs font-mono font-semibold text-slate-700 bg-slate-100 px-2 py-0.5 rounded">
                        {app.licenceNumber}
                      </span>
                    </div>
                  )}

                  {app.bio && (
                    <p className="text-sm text-slate-600 bg-slate-50 rounded-lg px-3 py-2 leading-relaxed">
                      {app.bio}
                    </p>
                  )}

                  {rejectUserId === app.userId ? (
                    <div className="space-y-2">
                      <textarea
                        aria-label="Rejection notes"
                        className="w-full text-sm border border-red-200 rounded-lg px-3 py-2 bg-white resize-none outline-none focus:border-red-400"
                        rows={2}
                        placeholder="Rejection notes (optional, sent to applicant)"
                        value={rejectNotes}
                        onChange={(e) => setRejectNotes(e.target.value)}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => void handleReject()}
                          disabled={busy}
                          className="text-xs font-semibold bg-red-600 hover:bg-red-700 text-white px-4 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {busy ? "Rejecting…" : "Confirm Reject"}
                        </button>
                        <button
                          onClick={() => { setRejectUserId(null); setRejectNotes(""); }}
                          className="text-xs text-slate-500 hover:text-slate-700 px-3 py-1.5"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => void handleApprove(app.userId)}
                        disabled={busy}
                        className="flex items-center gap-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <CheckCircle size={12} /> Approve
                      </button>
                      <button
                        onClick={() => { setRejectUserId(app.userId); setRejectNotes(""); }}
                        className="flex items-center gap-1.5 text-xs font-semibold bg-white hover:bg-red-50 text-red-600 border border-red-200 px-4 py-1.5 rounded-lg transition-colors"
                      >
                        <XCircle size={12} /> Reject
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Active drivers tab */}
        {tab === "active" && (
          <div className="space-y-3">
            {/* Search & filter bar */}
            <div className="flex flex-wrap gap-2">
              <div className="relative flex-1 min-w-[180px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  aria-label="Search by name or email"
                  placeholder="Search by name or email…"
                  value={driverSearch}
                  onChange={(e) => setDriverSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
              <select
                aria-label="Filter by vehicle type"
                value={driverVehicleFilter}
                onChange={(e) => setDriverVehicleFilter(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              >
                <option value="all">All vehicles</option>
                <option value="bike">Bike</option>
                <option value="car">Car</option>
                <option value="van">Van</option>
              </select>
              <select
                aria-label="Filter by driver status"
                value={driverStatusFilter}
                onChange={(e) => setDriverStatusFilter(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              >
                <option value="all">All statuses</option>
                <option value="available">Available</option>
                <option value="offline">Offline</option>
                <option value="deactivated">Deactivated</option>
              </select>
            </div>

            {loading ? (
              <p className="text-sm text-muted-foreground animate-pulse">Loading drivers…</p>
            ) : activeDrivers.length === 0 ? (
              <div className="text-center py-16 text-slate-400">
                <Users size={40} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm font-medium">No approved drivers yet</p>
              </div>
            ) : filteredDrivers.length === 0 ? (
              <div className="text-center py-16 text-slate-400">
                <Search size={40} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm font-medium">No drivers match your filters</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50 text-xs text-slate-500 font-semibold uppercase tracking-wide">
                      <th className="text-left px-4 py-3">Driver</th>
                      <th className="text-left px-4 py-3 hidden sm:table-cell">Vehicle</th>
                      <th className="text-left px-4 py-3 hidden md:table-cell">Town</th>
                      <th className="text-center px-4 py-3">Status</th>
                      <th className="text-right px-4 py-3 hidden sm:table-cell">Deliveries</th>
                      <th className="text-right px-4 py-3 hidden md:table-cell">Rating</th>
                      <th className="text-right px-4 py-3 hidden lg:table-cell">Approved</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDrivers.map((d, i) => (
                      <tr
                        key={d.userId}
                        onClick={() => void openDrawer(d)}
                        className={`cursor-pointer hover:bg-slate-50 transition-colors ${i !== 0 ? "border-t border-slate-100" : ""}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <WebAvatar name={d.userName ?? "?"} avatarUrl={d.userAvatarUrl} size={32} />
                            <div>
                              <p className="font-medium text-slate-800 flex items-center gap-1.5 flex-wrap">
                                {d.userName ?? "Unknown"}
                                {d.isHighValueTrusted && <span className="inline-flex items-center rounded-full border border-amber-400 bg-gradient-to-r from-amber-100 to-yellow-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800 dark:text-amber-300">Gold Trusted Driver</span>}
                              </p>
                              <p className="text-xs text-slate-400">{d.userEmail ?? ""}{d.userPhone ? ` · ${d.userPhone}` : ""}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className="flex items-center gap-1 text-slate-600">
                            {vehicleIcon(d.vehicleType)}
                            {vehicleLabel(d.vehicleType)}
                          </span>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell text-slate-500">{d.homeAddress ? `${d.homeAddress.town} · ${d.homeAddress.postcode}` : d.userTown ?? "—"}</td>
                        <td className="px-4 py-3 text-center">
                          {d.isDriverActive && d.isAvailable ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
                              <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
                              Available
                            </span>
                          ) : d.isDriverActive ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400">
                              <span className="w-2 h-2 rounded-full bg-slate-300 inline-block" />
                              Offline
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-400">
                              <span className="w-2 h-2 rounded-full bg-red-300 inline-block" />
                              Deactivated
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right hidden sm:table-cell font-semibold text-slate-700">
                          {d.completedCount}
                        </td>
                        <td className="px-4 py-3 text-right hidden md:table-cell">
                          {d.avgRating != null ? (
                            <span className="inline-flex items-center gap-1 text-sm font-semibold text-amber-600">
                              <Star size={12} className="fill-amber-400 text-amber-400" />
                              {Number(d.avgRating).toFixed(1)}
                              <span className="text-xs text-slate-400 font-normal">· {d.ratingCount}</span>
                            </span>
                          ) : (
                            <span className="text-xs text-slate-300 italic">No ratings yet</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right hidden lg:table-cell text-slate-400 text-xs">
                          {d.approvedAt ? new Date(d.approvedAt).toLocaleDateString("en-GB") : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Live deliveries tab */}
        {tab === "live" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-400 flex items-center gap-1.5">
                <RefreshCw size={11} /> Auto-refreshes every 15s
              </p>
              <button
                onClick={() => void fetchLive()}
                className="text-xs text-primary font-medium hover:underline"
              >
                Refresh now
              </button>
            </div>

            {loading ? (
              <p className="text-sm text-muted-foreground animate-pulse">Loading live deliveries…</p>
            ) : liveDeliveries.length === 0 ? (
              <div className="text-center py-16 text-slate-400">
                <Car size={40} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm font-medium">No live deliveries right now</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50 text-xs text-slate-500 font-semibold uppercase tracking-wide">
                      <th className="text-left px-4 py-3">Driver</th>
                      <th className="text-left px-4 py-3 hidden sm:table-cell">Store</th>
                      <th className="text-left px-4 py-3 hidden md:table-cell">Address</th>
                      <th className="text-left px-4 py-3 hidden sm:table-cell">Status</th>
                      <th className="text-right px-4 py-3 hidden sm:table-cell">Customer paid</th>
                      <th className="text-right px-4 py-3">Elapsed</th>
                      <th className="text-right px-4 py-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {liveDeliveries.map((d, i) => (
                      <tr key={d.orderId} className={`${i !== 0 ? "border-t border-slate-100" : ""}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs flex-shrink-0">
                              {initials(d.driverName)}
                            </div>
                            <div>
                              <p className="font-medium text-slate-800">{d.driverName ?? "—"}</p>
                              <p className="text-xs text-slate-400">Order #{d.orderId}</p>
                              <p className="text-xs font-semibold text-slate-700 sm:hidden">Customer paid {pence(d.customerPaidPence)}</p>
                              <div className="mt-1 space-y-0.5">
                                {(d.items ?? []).map((item, itemIndex) => {
                                  const options = optionNames(item.selectedOptions);
                                  return (
                                    <div key={`${item.name}-${itemIndex}`} className="text-xs text-slate-600">
                                      <span className="font-semibold">{item.quantity}×</span> {item.name} · {pence(item.pricePence * item.quantity)}
                                      {options.length > 0 && <span className="block text-[11px] text-slate-400">{options.join(", ")}</span>}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell text-slate-600">{d.storeName ?? "—"}</td>
                        <td className="px-4 py-3 hidden md:table-cell text-slate-500 text-xs max-w-[140px] truncate">
                          {d.deliveryAddress ?? "—"}
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          {d.status === "out_for_delivery" ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600">
                              <span className="w-2 h-2 rounded-full bg-blue-500 inline-block animate-pulse" />
                              In transit
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600">
                              <span className="w-2 h-2 rounded-full bg-amber-400 inline-block animate-pulse" />
                              Awaiting pickup
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right hidden sm:table-cell font-semibold text-slate-700">
                          {pence(d.customerPaidPence)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="flex items-center justify-end gap-1 text-amber-600 font-medium text-xs">
                            <Clock size={12} />
                            {d.pickedUpAt ? elapsed(d.pickedUpAt) : elapsed(d.createdAt)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex flex-col items-end gap-1.5">
                            {d.driverId && (
                              <button
                                onClick={() => { setMsgTarget({ driverId: d.driverId!, driverName: d.driverName ?? "Driver" }); setMsgBody(""); }}
                                className="text-xs font-semibold text-blue-600 hover:text-blue-700 border border-blue-200 hover:bg-blue-50 px-2 py-1 rounded-lg transition-colors flex items-center gap-1"
                              >
                                <MessageCircle size={11} />
                                Message
                              </button>
                            )}
                            <button
                              onClick={() => void handleCancelDelivery(d.orderId)}
                              disabled={busy}
                              className="text-xs font-semibold text-red-600 hover:text-red-700 border border-red-200 hover:bg-red-50 px-2 py-1 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1"
                            >
                              <AlertTriangle size={11} />
                              Unassign
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {/* Dispatch failures tab */}
        {tab === "dispatch" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600">
                Orders where no driver was found. Re-dispatch sends fresh offers to online drivers; Cancel marks the order for manual handling.
              </p>
              <button
                onClick={() => void fetchDispatchFailures()}
                className="text-xs text-primary font-medium hover:underline flex-shrink-0"
              >
                Refresh
              </button>
            </div>

            {loading ? (
              <p className="text-sm text-muted-foreground animate-pulse">Loading…</p>
            ) : dispatchFailures.length === 0 ? (
              <div className="text-center py-16 text-slate-400">
                <CheckCircle size={40} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm font-medium">No dispatch failures</p>
                <p className="text-xs mt-1">All hub orders have been successfully dispatched</p>
              </div>
            ) : (
              <div className="space-y-3">
                {dispatchFailures.map((d) => (
                  <div key={d.orderId} className="bg-white border border-red-200 rounded-xl p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <AlertTriangle size={14} className="text-red-500 flex-shrink-0" />
                          <p className="font-semibold text-sm text-slate-800">
                            Order #{d.orderId} — {d.storeName ?? "Unknown store"}
                          </p>
                        </div>
                        <p className="text-xs text-slate-500">{d.deliveryAddress ?? "—"}</p>
                        {d.customerName && (
                          <p className="text-xs text-slate-400">Customer: {d.customerName}</p>
                        )}
                        <p className="text-xs text-slate-400 mt-0.5">
                          Placed {new Date(d.createdAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-slate-800">{pence(d.totalPence)}</p>
                        <p className="text-xs text-slate-400">Delivery fee: {pence(d.deliveryFeePence)}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={() => void handleRedispatch(d.orderId)}
                        disabled={busy}
                        className="flex items-center gap-1.5 text-xs font-semibold bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <RefreshCw size={12} />
                        Re-dispatch to available drivers
                      </button>
                      <button
                        onClick={() => void handleCancelFailedOrder(d.orderId)}
                        disabled={busy}
                        className="flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:text-red-700 border border-red-200 hover:bg-red-50 px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <XCircle size={12} />
                        Cancel order
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Map tab — split: Leaflet map + driver control panel */}
        {tab === "map" && (
          <div className="flex flex-col lg:flex-row gap-4" style={{ minHeight: 560 }}>
            {/* Map */}
            <div className="flex-1 min-w-0 rounded-xl overflow-hidden border border-border" style={{ minHeight: 420 }}>
              <AvailableDriversMap mode="admin" height={560} />
            </div>

            {/* Driver list panel */}
            <div className="w-full lg:w-80 flex flex-col gap-3 overflow-y-auto" style={{ maxHeight: 560 }}>
              <div className="flex items-center justify-between px-1">
                <span className="text-sm font-semibold text-slate-700">
                  All Drivers ({activeDrivers.length})
                </span>
                <span className="text-xs text-muted-foreground">
                  {activeDrivers.filter((d) => d.isOnline).length} online
                </span>
              </div>

              {loading && activeDrivers.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-8">Loading…</div>
              )}

              {activeDrivers.map((d) => (
                <div
                  key={d.userId}
                  className="bg-white border border-border rounded-xl p-3 flex flex-col gap-2 shadow-sm"
                >
                  <div className="flex items-center gap-2.5">
                    <WebAvatar name={d.userName ?? "?"} avatarUrl={d.userAvatarUrl} size={36} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-slate-800 truncate">{d.userName ?? "Unknown"}</div>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        {vehicleIcon(d.vehicleType)}
                        {vehicleLabel(d.vehicleType)}
                        {d.userPhone && <span>· {d.userPhone}</span>}
                        {d.userTown && <span>· {d.userTown}</span>}
                      </div>
                    </div>
                    <div className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${d.isOnline ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${d.isOnline ? "bg-green-500" : "bg-slate-400"}`} />
                      {d.isOnline ? "Online" : "Offline"}
                    </div>
                  </div>

                  {/* Today's stats */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-slate-50 rounded-lg px-2.5 py-1.5 text-center">
                      <div className="text-lg font-bold text-primary">{d.todayDeliveryCount}</div>
                      <div className="text-xs text-muted-foreground">deliveries today</div>
                    </div>
                    <div className="bg-slate-50 rounded-lg px-2.5 py-1.5 text-center">
                      <div className="text-lg font-bold text-green-600">{pence(d.todayEarningsPence)}</div>
                      <div className="text-xs text-muted-foreground">earned today</div>
                    </div>
                  </div>

                  {d.lastLocationAt && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock size={11} />
                      Last seen {elapsed(d.lastLocationAt)} ago
                    </div>
                  )}

                  {/* Controls */}
                  <div className="flex gap-2 pt-1">
                    {d.isOnline ? (
                      <button
                        onClick={() => void handleForceOnline(d.userId, false)}
                        className="flex-1 text-xs font-semibold text-red-600 border border-red-200 hover:bg-red-50 rounded-lg py-1.5 transition-colors"
                      >
                        Set Offline
                      </button>
                    ) : (
                      <button
                        onClick={() => void handleForceOnline(d.userId, true)}
                        className="flex-1 text-xs font-semibold text-green-700 border border-green-200 hover:bg-green-50 rounded-lg py-1.5 transition-colors"
                      >
                        Set Online
                      </button>
                    )}
                    <button
                      onClick={() => openDrawer(d)}
                      className="flex-1 text-xs font-semibold text-slate-600 border border-border hover:bg-slate-50 rounded-lg py-1.5 transition-colors"
                    >
                      View Profile
                    </button>
                  </div>
                </div>
              ))}

              {!loading && activeDrivers.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-8">No approved drivers</div>
              )}
            </div>
          </div>
        )}
        {/* Hub Dash tab */}
        {tab === "hubdash" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
                <Package size={16} className="text-indigo-600" />
                Hub Dash Deliveries
              </h2>
              <button onClick={() => void fetchHubDash()} className="flex items-center gap-1.5 text-xs font-medium text-slate-600 border border-slate-200 px-2.5 py-1.5 rounded-lg hover:bg-slate-50 transition-colors">
                <RefreshCw size={12} /> Refresh
              </button>
            </div>

            {hubDashLoading && hubDashItems.length === 0 && (
              <div className="text-sm text-slate-400 py-4">Loading…</div>
            )}

            {hubDashItems.length === 0 && !hubDashLoading && (
              <div className="text-sm text-slate-400 py-8 text-center">No Hub Dash deliveries yet</div>
            )}

            {hubDashItems.length > 0 && (
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">ID</th>
                      <th className="px-3 py-2 text-left font-semibold">Status</th>
                      <th className="px-3 py-2 text-left font-semibold">Customer</th>
                      <th className="px-3 py-2 text-left font-semibold">Driver</th>
                      <th className="px-3 py-2 text-left font-semibold">Route</th>
                      <th className="px-3 py-2 text-right font-semibold">Paid</th>
                      <th className="px-3 py-2 text-right font-semibold">Driver earns</th>
                      <th className="px-3 py-2 text-right font-semibold">Tip</th>
                      <th className="px-3 py-2 text-center font-semibold">Rating</th>
                      <th className="px-3 py-2 text-left font-semibold">Date</th>
                      <th className="px-3 py-2 text-center font-semibold">Refund</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {hubDashItems.map(r => {
                      const statusColors: Record<string, string> = {
                        dispatching: "bg-amber-100 text-amber-700",
                        accepted: "bg-blue-100 text-blue-700",
                        picked_up: "bg-blue-100 text-blue-700",
                        delivered: "bg-green-100 text-green-700",
                        cancelled: "bg-slate-100 text-slate-500",
                        no_drivers: "bg-red-100 text-red-700",
                        refunded: "bg-red-100 text-red-600",
                      };
                      return (
                        <tr key={r.id} className="hover:bg-slate-50">
                          <td className="px-3 py-2 font-bold text-foreground">#{r.id}</td>
                          <td className="px-3 py-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${statusColors[r.status] ?? "bg-slate-100 text-slate-600"}`}>
                              {r.status.replace(/_/g, " ")}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-slate-700">{r.customerName}</td>
                          <td className="px-3 py-2 text-slate-600">
                            {r.driverName ? (
                              <span className="flex items-center gap-1">
                                <Navigation size={11} className="text-blue-500" />
                                {r.driverName}
                              </span>
                            ) : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="px-3 py-2 text-xs text-slate-600">
                            <div>{r.pickupAddress}</div>
                            <div className="text-slate-400">→ {r.dropoffAddress}</div>
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-slate-700">{pence(r.quotedFeePence)}</td>
                          <td className="px-3 py-2 text-right font-semibold text-green-700">{pence(r.driverEarningsPence)}</td>
                          <td className="px-3 py-2 text-right text-pink-600 font-medium">
                            {r.tipPence > 0 ? pence(r.tipPence) : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="px-3 py-2 text-center text-amber-600 font-semibold">
                            {r.rating != null ? `${r.rating}★` : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="px-3 py-2 text-slate-400 whitespace-nowrap text-xs">{new Date(r.createdAt).toLocaleDateString("en-GB")}</td>
                          <td className="px-3 py-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {["accepted", "dispatching", "no_drivers", "waiting_for_drivers"].includes(r.status) && (
                                <button
                                  onClick={async () => {
                                    if (!confirm(`Unassign driver from delivery #${r.id} and redispatch to next available driver?`)) return;
                                    setHubDashReassigningId(r.id);
                                    try {
                                      const res = await fetch(`/api/admin/item-delivery/${r.id}/force-reassign`, { method: "POST", headers: authHeaders() });
                                      const d = await res.json() as { error?: string;pending?:boolean };
                                      if (!res.ok) { toast({ title: d.error ?? "Reassign failed", variant: "destructive" }); }
                                      else { toast({ title: `Delivery #${r.id} reassigned — dispatching to next driver` }); void fetchHubDash(); }
                                    } catch { toast({ title: "Network error", variant: "destructive" }); }
                                    finally { setHubDashReassigningId(null); }
                                  }}
                                  disabled={hubDashReassigningId === r.id}
                                  className="p-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-600 disabled:opacity-50 transition-colors"
                                  title="Force Reassign — unassign driver and redispatch"
                                >
                                  {hubDashReassigningId === r.id
                                    ? <RefreshCw size={13} className="animate-spin" />
                                    : <Navigation size={13} />
                                  }
                                </button>
                              )}
                              {r.status !== "refunded" && r.status !== "cancelled" ? (
                                <button
                                  onClick={async () => {
                                    if (!confirm(`Refund ${pence(r.quotedFeePence + (r.grossTipPence??r.tipPence))} to ${r.customerName} for delivery #${r.id}? Completed driver earnings are preserved.`)) return;
                                    setHubDashRefundingId(r.id);
                                    try {
                                      const res = await fetch(`/api/admin/item-delivery/${r.id}/refund`, { method: "POST", headers: authHeaders() });
                                      const d = await res.json() as { error?: string;pending?:boolean };
                                      if (!res.ok) { toast({ title: d.error ?? "Refund failed", variant: "destructive" }); }
                                      else { toast({ title: d.pending?`Delivery #${r.id} refund is pending`:`Delivery #${r.id} refund confirmed` }); void fetchHubDash(); }
                                    } catch { toast({ title: "Network error", variant: "destructive" }); }
                                    finally { setHubDashRefundingId(null); }
                                  }}
                                  disabled={hubDashRefundingId === r.id}
                                  className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 disabled:opacity-50 transition-colors"
                                  title="Refund"
                                >
                                  {hubDashRefundingId === r.id
                                    ? <RefreshCw size={13} className="animate-spin" />
                                    : <RotateCcw size={13} />
                                  }
                                </button>
                              ) : (
                                <span className="text-xs text-red-500 font-semibold">{r.status === "refunded" ? "Refunded" : "—"}</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Email All Drivers dialog */}
      {emailDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
                <Mail size={16} className="text-primary" /> Email All Drivers
              </h2>
              <button
                onClick={() => setEmailDialogOpen(false)}
                className="text-slate-400 hover:text-slate-600 text-xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="text-xs text-slate-500">
              Sends a branded Ayrshire Hub email to all active drivers via BCC — drivers will not see each other's addresses.
            </p>
            <div className="space-y-3">
              <div>
                <label htmlFor="email-all-subject" className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1">Subject</label>
                <input
                  id="email-all-subject"
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="e.g. Important update for drivers"
                  className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
              <div>
                <label htmlFor="email-all-body" className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1">Message</label>
                <textarea
                  id="email-all-body"
                  rows={6}
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  placeholder="Write your message here…"
                  className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setEmailDialogOpen(false)}
                className="text-sm text-slate-500 hover:text-slate-700 px-4 py-2"
              >
                Cancel
              </button>
              <button
                onClick={() => void handleEmailAll()}
                disabled={sendingEmail || !emailSubject.trim() || !emailBody.trim()}
                className="flex items-center gap-1.5 text-sm font-semibold bg-primary hover:bg-primary/90 text-white px-5 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                <Send size={13} />
                {sendingEmail ? "Sending…" : "Send to All Drivers"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message driver dialog */}
      {msgTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageCircle size={18} className="text-blue-600" />
                <h3 className="font-bold text-slate-800">Message {msgTarget.driverName}</h3>
              </div>
              <button onClick={() => setMsgTarget(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <p className="text-xs text-slate-500">Sent as a push notification to the driver's phone.</p>
            <textarea
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-300"
              rows={4}
              placeholder="Type your message…"
              value={msgBody}
              onChange={(e) => setMsgBody(e.target.value)}
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setMsgTarget(null)} className="text-sm text-slate-500 hover:text-slate-700 px-4 py-2">Cancel</button>
              <button
                onClick={() => void sendDriverMessage()}
                disabled={sendingMsg || !msgBody.trim()}
                className="flex items-center gap-1.5 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                <Send size={13} />
                {sendingMsg ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Sheet open={!!drawerDriver} onOpenChange={(open) => { if (!open) setDrawerDriver(null); }}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {drawerDriver && (
            <>
              <SheetHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <WebAvatar name={drawerDriver.userName ?? "?"} avatarUrl={drawerDriver.userAvatarUrl} size={48} />
                  <div>
                    <SheetTitle className="text-base">{drawerDriver.userName ?? "Driver"}</SheetTitle>
                    {drawerDriver.isHighValueTrusted && <span className="mt-1 inline-flex items-center rounded-full border border-amber-400 bg-gradient-to-r from-amber-100 to-yellow-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-800 dark:text-amber-300 shadow-sm">Gold Trusted Driver</span>}
                    <p className="text-xs text-slate-500">{drawerDriver.userEmail ?? ""}{drawerDriver.userPhone ? ` · ${drawerDriver.userPhone}` : ""}</p>
                  </div>
                </div>

                <div className="rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 p-4 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-bold text-amber-900 dark:text-amber-300">Gold Trusted Driver</p>
                    <p className="text-xs text-amber-700 mt-0.5">Allows this driver to receive and accept high-value jobs on both delivery legs.</p>
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void setHighValueTrust(!drawerDriver.isHighValueTrusted)}
                    className={`shrink-0 rounded-lg px-3 py-2 text-xs font-bold transition-colors ${drawerDriver.isHighValueTrusted ? "border border-amber-300 bg-white text-amber-800 dark:text-amber-300 hover:bg-amber-100" : "bg-amber-500 text-white hover:bg-amber-600"}`}
                  >
                    {drawerDriver.isHighValueTrusted ? "Remove" : "Award Badge"}
                  </button>
                </div>
              </SheetHeader>

              <div className="space-y-5">
                {/* Profile info */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-1">Vehicle</p>
                    <p className="text-sm font-semibold flex items-center gap-1.5 text-slate-700">
                      {vehicleIcon(drawerDriver.vehicleType)}
                      {vehicleLabel(drawerDriver.vehicleType)}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-1">Status</p>
                    {drawerDriver.isDriverActive && drawerDriver.isAvailable ? (
                      <p className="text-sm font-semibold text-emerald-600">Available</p>
                    ) : drawerDriver.isDriverActive ? (
                      <p className="text-sm font-semibold text-slate-400">Offline</p>
                    ) : (
                      <p className="text-sm font-semibold text-red-400">Deactivated</p>
                    )}
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-1">Town</p>
                    <p className="text-sm font-semibold text-slate-700">{drawerDriver.userTown ?? "—"}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-1">Approved</p>
                    <p className="text-sm font-semibold text-slate-700">
                      {drawerDriver.approvedAt ? new Date(drawerDriver.approvedAt).toLocaleDateString("en-GB") : "—"}
                    </p>
                  </div>
                </div>

                {drawerDriver.bio && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Bio</p>
                    <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 rounded-lg px-3 py-2">{drawerDriver.bio}</p>
                  </div>
                )}

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Home address · compliance records</p>
                  {drawerDriver.homeAddress ? <><p className="mt-2 text-sm font-semibold text-slate-900">{[drawerDriver.homeAddress.line1,drawerDriver.homeAddress.line2,drawerDriver.homeAddress.town,drawerDriver.homeAddress.postcode].filter(Boolean).join(", ")}</p><p className="mt-2 text-xs text-slate-500">Confirmed {new Date(drawerDriver.homeAddress.confirmedAt).toLocaleString("en-GB")}</p></> : <p className="mt-2 text-sm text-slate-600">Awaiting confirmation when this driver next goes online.</p>}
                </div>

                {/* Earnings summary */}
                <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-emerald-600 font-semibold">Total Earnings</p>
                    <p className="text-2xl font-bold text-emerald-700">{pence(historyTotal)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-emerald-600 font-semibold">Deliveries</p>
                    <p className="text-2xl font-bold text-emerald-700">{history.length}</p>
                  </div>
                </div>

                {/* Rating stat */}
                {drawerDriver.avgRating != null ? (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 rounded-lg p-3 flex items-center gap-3">
                    <Star size={20} className="fill-amber-400 text-amber-400 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-amber-600 font-semibold">Average Rating</p>
                      <p className="text-lg font-bold text-amber-700">
                        {Number(drawerDriver.avgRating).toFixed(1)}
                        <span className="text-sm font-normal text-amber-500 ml-1">/ 5</span>
                        <span className="text-xs font-normal text-slate-400 ml-2">from {drawerDriver.ratingCount} {drawerDriver.ratingCount === 1 ? "review" : "reviews"}</span>
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-slate-50 border border-slate-100 rounded-lg p-3 flex items-center gap-2 text-slate-400">
                    <Star size={16} className="flex-shrink-0" />
                    <p className="text-xs">No customer ratings yet</p>
                  </div>
                )}

                {/* Delivery history */}
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Delivery History</p>
                  {historyLoading ? (
                    <p className="text-xs text-slate-400 animate-pulse">Loading history…</p>
                  ) : history.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">No completed deliveries yet</p>
                  ) : (
                    <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                      {history.map((row) => (
                        <div key={row.orderId} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-3 py-2">
                          <div>
                            <p className="font-medium text-slate-700">{row.storeName ?? "Order"} #{row.orderId}</p>
                            <p className="text-slate-400">
                              {row.deliveredAt ? new Date(row.deliveredAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                            </p>
                          </div>
                          <p className="font-semibold text-emerald-600">{pence(row.earningsPence)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Driver credits */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Account Credits</p>
                    <button
                      onClick={() => {
                        setCreditFormOpen((v) => !v);
                        setCreditAmountPounds("");
                        setCreditReason(CREDIT_REASONS[0]);
                        setCreditNote("");
                      }}
                      className="flex items-center gap-1 text-xs font-semibold text-emerald-600 border border-emerald-200 hover:bg-emerald-50 px-2.5 py-1 rounded-lg transition-colors"
                    >
                      <PlusCircle size={12} /> Add Credit
                    </button>
                  </div>

                  {creditFormOpen && (
                    <div className="border border-emerald-200 bg-emerald-50/60 rounded-xl p-4 space-y-3">
                      <p className="text-xs font-semibold text-emerald-800">Issue Credit to {drawerDriver.userName}</p>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1">Amount (£)</label>
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          placeholder="e.g. 5.00"
                          value={creditAmountPounds}
                          onChange={(e) => setCreditAmountPounds(e.target.value)}
                          className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1">Reason</label>
                        <select
                          value={creditReason}
                          onChange={(e) => setCreditReason(e.target.value)}
                          className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400"
                        >
                          {CREDIT_REASONS.map((r) => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1">Note (optional)</label>
                        <textarea
                          rows={2}
                          placeholder="Additional details…"
                          value={creditNote}
                          onChange={(e) => setCreditNote(e.target.value)}
                          className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white resize-none focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400"
                        />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => setCreditFormOpen(false)} className="text-xs text-slate-500 hover:text-slate-700 px-3 py-1.5">Cancel</button>
                        <button
                          onClick={() => void submitCredit(drawerDriver.userId, drawerDriver.userName ?? "Driver")}
                          disabled={submittingCredit || !creditAmountPounds.trim()}
                          className="flex items-center gap-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                        >
                          <PlusCircle size={12} />
                          {submittingCredit ? "Adding…" : "Confirm Credit"}
                        </button>
                      </div>
                    </div>
                  )}

                  {loadingCredits ? (
                    <p className="text-xs text-slate-400 animate-pulse">Loading…</p>
                  ) : driverCredits.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">No credits issued yet</p>
                  ) : (
                    <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                      {driverCredits.map((c) => (
                        <div key={c.id} className="flex items-start justify-between text-xs bg-emerald-50 rounded-lg px-3 py-2 gap-2">
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-700">{c.reason}</p>
                            {c.note && <p className="text-slate-500 truncate">{c.note}</p>}
                            <p className="text-slate-400">{new Date(c.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}{c.createdByName ? ` · ${c.createdByName}` : ""}</p>
                          </div>
                          <p className="font-bold text-emerald-600 flex-shrink-0">+{pence(c.amountPence)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Admin notes */}
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                    Admin Notes {savingNotes && <span className="text-slate-400 normal-case font-normal">(saving…)</span>}
                  </p>
                  <textarea
                    aria-label="Admin notes"
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white resize-none outline-none focus:border-primary transition-colors"
                    rows={3}
                    placeholder="Internal notes about this driver…"
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    onBlur={() => void saveNotes()}
                  />
                </div>

                {/* Deactivate */}
                <div className="border-t border-slate-100 pt-4">
                  {!deactivateConfirm ? (
                    <button
                      onClick={() => setDeactivateConfirm(true)}
                      disabled={!drawerDriver.isDriverActive}
                      className="flex items-center gap-1.5 text-sm font-semibold text-red-600 border border-red-200 hover:bg-red-50 px-4 py-2 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <XCircle size={14} />
                      {drawerDriver.isDriverActive ? "Deactivate Driver" : "Driver already deactivated"}
                    </button>
                  ) : (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
                      <p className="text-sm font-semibold text-red-700 flex items-center gap-1.5">
                        <AlertTriangle size={14} />
                        Confirm deactivation
                      </p>
                      <p className="text-xs text-red-600">
                        This will remove their driver access. They can re-apply later if needed.
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => void handleDeactivate()}
                          disabled={deactivating}
                          className="text-xs font-semibold bg-red-600 hover:bg-red-700 text-white px-4 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {deactivating ? "Deactivating…" : "Yes, Deactivate"}
                        </button>
                        <button
                          onClick={() => setDeactivateConfirm(false)}
                          className="text-xs text-slate-500 hover:text-slate-700 px-3 py-1.5"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </AdminLayout>
  );
}
